import * as React from 'react';
import { RefreshCw, Save, Loader2, Check, AlertCircle, Wifi } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { getAsrConfig, saveAsrConfig, testAsrConnection } from '@/api/client';

type AsrProvider = {
  name: string;
  fields: string[];
};

export function AsrConfigTab() {
  const [providers, setProviders] = React.useState<Record<string, AsrProvider>>({});
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [testing, setTesting] = React.useState<string | null>(null);
  const [testResult, setTestResult] = React.useState<{ provider: string; message: string; ok: boolean } | null>(null);
  const [saveMsg, setSaveMsg] = React.useState('');
  const [form, setForm] = React.useState<Record<string, Record<string, string>>>({});

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAsrConfig();
      setProviders(data.providers || {});
      // 初始化表单
      const f: Record<string, Record<string, string>> = {};
      for (const [key, p] of Object.entries(data.providers || {})) {
        f[key] = {};
        for (const field of (p as AsrProvider).fields) {
          f[key][field] = data.config?.[key]?.[field] || '';
        }
      }
      setForm(f);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const updateField = React.useCallback((provider: string, field: string, value: string) => {
    setForm((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], [field]: value },
    }));
  }, []);

  const handleSave = React.useCallback(async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      await saveAsrConfig(form);
      setSaveMsg('已保存');
      setTimeout(() => setSaveMsg(''), 2000);
    } catch (err) {
      setSaveMsg(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }, [form]);

  const handleTest = React.useCallback(async (provider: string) => {
    setTesting(provider);
    setTestResult(null);
    try {
      const data = await testAsrConnection(provider);
      setTestResult({ provider, message: data.message, ok: true });
    } catch (err) {
      setTestResult({ provider, message: err instanceof Error ? err.message : '连接失败', ok: false });
    } finally {
      setTesting(null);
    }
  }, []);

  if (loading) {
    return <Skeleton className="h-48 w-full rounded-xl" />;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>ASR 转录配置</CardTitle>
            <CardDescription>配置语音识别服务商，用于视频文案转录</CardDescription>
          </div>
          <Button variant="ghost" size="icon" onClick={load} title="刷新">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {Object.entries(providers).map(([key, p]) => (
          <div key={key} className="rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="font-medium">{p.name}</h4>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => handleTest(key)}
                disabled={testing === key}
              >
                {testing === key ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Wifi className="h-3.5 w-3.5" />
                )}
                测试连接
              </Button>
            </div>
            <div className="space-y-2">
              {p.fields.map((field) => (
                <div key={field}>
                  <label className="mb-1 block text-xs text-muted-foreground">{field}</label>
                  <Input
                    type={field.includes('key') || field.includes('secret') ? 'password' : 'text'}
                    className="h-8 text-sm"
                    value={form[key]?.[field] || ''}
                    onChange={(e) => updateField(key, field, e.target.value)}
                    placeholder={`输入 ${field}`}
                  />
                </div>
              ))}
            </div>
            {testResult && testResult.provider === key && (
              <div className={`mt-2 flex items-center gap-1.5 text-xs ${testResult.ok ? 'text-emerald-500' : 'text-destructive'}`}>
                {testResult.ok ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
                {testResult.message}
              </div>
            )}
          </div>
        ))}

        <div className="flex items-center gap-3">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            保存全部配置
          </Button>
          {saveMsg && (
            <span className={`inline-flex items-center gap-1 text-sm ${saveMsg === '已保存' ? 'text-emerald-500' : 'text-destructive'}`}>
              {saveMsg === '已保存' ? <Check className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
              {saveMsg}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}